import { InfrastructureType } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import {
  PaginatedQueryConfig,
  usePaginatedQuery,
  PaginationResponse,
  PaginationParams,
  UsePaginatedQueryOptions,
} from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';

import { api } from '@/lib/api-client';
import { ItemInfrastructureEntity } from '@/types/api';

export interface GetItemInfrastructuresParams {
  infrastructureType?: InfrastructureType;
}

export const getItemInfrastructures = async (
  orgId: string,
  projectId: string,
  itemId: string,
  params?: GetItemInfrastructuresParams & PaginationParams,
): Promise<PaginationResponse<ItemInfrastructureEntity>> => {
  const { data } = await api.get<
    undefined,
    ApiResponse<PaginationResponse<ItemInfrastructureEntity>>
  >(`/orgs/${orgId}/projects/${projectId}/items/${itemId}/infrastructures`, {
    params,
  });
  return data;
};

export const getItemInfrastructuresQueryKey = (
  orgId?: string,
  projectId?: string,
  itemId?: string,
  params?: GetItemInfrastructuresParams,
) => {
  const queryKey: unknown[] = ['item-infrastructures'];
  if (orgId) queryKey.push('org', orgId);
  if (projectId) queryKey.push('project', projectId);
  if (itemId) queryKey.push('item', itemId);
  if (params) queryKey.push(params);
  return queryKey;
};

export const getItemInfrastructuresQueryOptions = (
  orgId?: string,
  projectId?: string,
  itemId?: string,
  params?: GetItemInfrastructuresParams,
): UsePaginatedQueryOptions<ItemInfrastructureEntity> => {
  return {
    queryKey: getItemInfrastructuresQueryKey(orgId, projectId, itemId, params),
    queryFn: ({ pageParam }) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      getItemInfrastructures(orgId!, projectId!, itemId!, {
        ...params,
        ...pageParam,
      }),
    enabled: !!orgId && !!projectId && !!itemId,
  };
};

export type UseItemInfrastructuresOptions = {
  queryConfig?: PaginatedQueryConfig<typeof getItemInfrastructuresQueryOptions>;
} & GetItemInfrastructuresParams;

export const useItemInfrastructures = (
  itemId?: string,
  { queryConfig, ...params }: UseItemInfrastructuresOptions = {},
) => {
  const { orgId, projectId } = useScopeIds('org', 'project');
  return usePaginatedQuery({
    ...getItemInfrastructuresQueryOptions(orgId, projectId, itemId, params),
    ...queryConfig,
  });
};
